import { canAccess } from '../../../../authorization/canAccess';
import { canAccessResource } from '../../../../authorization/canAccessResource';
import { hasRecordAction } from '../../../../authorization/hasRecordAction';
import { AUTHZ_FEATURES } from '../../../../constants/authz/features.constants';
import { globalpermissions } from '../../../../Services/Permission.js';

const RECORD = AUTHZ_FEATURES.RECORDS;

const LEGACY_DOWNLOAD_PERMISSIONS = [
  globalpermissions?.leadDashboard?.downloadLeadColumn,
  globalpermissions?.navBar?.menuCampaignLeads,
  globalpermissions?.pagePermissions?.campaignLeadsPageAccess,
].filter(Boolean);

function hasLegacyPermission(userInfo, legacyUserHasPermission, keys = []) {
  if (typeof legacyUserHasPermission === 'function') {
    return legacyUserHasPermission(keys.filter(Boolean));
  }
  const legacyPermissions = userInfo?.profile?.permissions || {};
  return keys.some((key) => Boolean(key && legacyPermissions[key]));
}

function canViewCampaign(authz, { workspaceId, accountId } = {}) {
  if (!workspaceId) return false;
  return canAccessResource(authz, {
    type: 'campaign',
    id: workspaceId,
    action: 'view',
    accountId,
  });
}

function hasRecordPermission(authz, { workspaceId, accountId } = {}, codename) {
  if (!workspaceId) return false;
  if (authz?.isFullAccess) return true;
  if (!authz?.isAuthzV2Enabled) {
    return true;
  }
  if (!canViewCampaign(authz, { workspaceId, accountId })) {
    return false;
  }
  return hasRecordAction(authz, workspaceId, codename, accountId);
}

export function canCreateRecord(authz, params = {}) {
  if (!params.workspaceId) return false;
  if (authz?.isFullAccess) return true;

  if (!authz?.isAuthzV2Enabled) {
    return hasLegacyPermission(params.userInfo, params.legacyUserHasPermission, [
      globalpermissions?.leadDashboard?.enterLeadColumn,
    ]);
  }

  return hasRecordPermission(authz, params, RECORD.CREATE);
}

export function canEditRecord(authz, params = {}) {
  if (!params.workspaceId) return false;
  if (authz?.isFullAccess) return true;

  if (!authz?.isAuthzV2Enabled) {
    return hasLegacyPermission(params.userInfo, params.legacyUserHasPermission, [
      globalpermissions?.leadDashboard?.enterLeadColumn,
    ]);
  }

  return hasRecordPermission(authz, params, RECORD.UPDATE);
}

export function canDeleteRecord(authz, params = {}) {
  if (!params.workspaceId) return false;
  if (authz?.isFullAccess) return true;

  if (!authz?.isAuthzV2Enabled) {
    return hasLegacyPermission(params.userInfo, params.legacyUserHasPermission, [
      globalpermissions?.leadDashboard?.deleteLeadColumn,
    ]);
  }

  return hasRecordPermission(authz, params, RECORD.DELETE);
}

export function canBulkUpdateRecords(authz, params = {}) {
  return canEditRecord(authz, params);
}

export function canMoveRecords(authz, params = {}) {
  if (!params.workspaceId) return false;
  if (authz?.isFullAccess) return true;

  if (!authz?.isAuthzV2Enabled) {
    return hasLegacyPermission(params.userInfo, params.legacyUserHasPermission, [
      globalpermissions?.leadDashboard?.moveLeads,
    ]);
  }

  return hasRecordPermission(authz, params, RECORD.MOVE);
}

export function canUseRecordFilters(authz, params = {}) {
  if (!params.workspaceId) return false;
  if (authz?.isFullAccess) return true;

  if (!authz?.isAuthzV2Enabled) {
    return true;
  }

  return hasRecordPermission(authz, params, RECORD.VIEW);
}

export function canDownloadRecords(authz, params = {}) {
  if (!params.workspaceId) return false;
  if (authz?.isFullAccess) return true;

  if (!authz?.isAuthzV2Enabled) {
    return hasLegacyPermission(
      params.userInfo,
      params.legacyUserHasPermission,
      LEGACY_DOWNLOAD_PERMISSIONS
    );
  }

  return hasRecordPermission(authz, params, RECORD.DOWNLOAD);
}

export function canImportRecords(authz, params = {}) {
  if (!params.workspaceId) return false;
  if (authz?.isFullAccess) return true;

  if (!authz?.isAuthzV2Enabled) {
    return hasLegacyPermission(params.userInfo, params.legacyUserHasPermission, [
      globalpermissions?.leadDashboard?.importLeadColumn,
    ]);
  }

  return hasRecordPermission(authz, params, RECORD.IMPORT);
}

export function getDownloadPermissionKey(authz, params = {}) {
  if (!params.workspaceId) return null;
  if (authz?.isFullAccess) return 'isFullAccess';
  if (authz?.isAuthzV2Enabled) {
    return RECORD.DOWNLOAD;
  }

  return LEGACY_DOWNLOAD_PERMISSIONS.find((key) =>
    hasLegacyPermission(params.userInfo, params.legacyUserHasPermission, [key])
  ) || null;
}

export function canEnterRecords(authz, params = {}) {
  return canCreateRecord(authz, params);
}

export function canEditRecordForm(authz, params = {}) {
  if (!params.workspaceId) return false;
  if (authz?.isFullAccess) return true;

  if (!authz?.isAuthzV2Enabled) {
    return hasLegacyPermission(params.userInfo, params.legacyUserHasPermission, [
      globalpermissions?.leadDashboard?.createWebForm,
    ]);
  }

  return canAccessResource(authz, {
    type: 'campaign',
    id: params.workspaceId,
    action: 'edit',
    accountId: params.accountId,
  });
}

export function canViewReports(authz, params = {}) {
  return canUseRecordFilters(authz, params);
}

export function canViewHelp(authz, params = {}) {
  return canUseRecordFilters(authz, params);
}

export function canAddComment(authz, params = {}) {
  if (!params.workspaceId) return false;
  if (authz?.isFullAccess) return true;

  if (!authz?.isAuthzV2Enabled) {
    return hasLegacyPermission(params.userInfo, params.legacyUserHasPermission, [
      globalpermissions?.leadFormPage?.addComment,
    ]);
  }

  return hasRecordPermission(authz, params, RECORD.ADD_COMMENT);
}

export function canQuickCallRecord(authz, params = {}) {
  if (!params.workspaceId) return false;
  if (authz?.isFullAccess) return true;

  if (!authz?.isAuthzV2Enabled) {
    return hasLegacyPermission(params.userInfo, params.legacyUserHasPermission, [
      globalpermissions?.leadFormPage?.quickCall,
    ]);
  }

  return hasRecordPermission(authz, params, RECORD.QUICK_CALL);
}

export function canManageRecordAsAdmin(authz, params = {}) {
  if (!params.workspaceId) return false;
  if (authz?.isFullAccess) return true;

  if (!authz?.isAuthzV2Enabled) {
    return Boolean(params.userInfo?.is_superuser);
  }

  return hasRecordPermission(authz, params, RECORD.MANAGE_ADMIN);
}

export function canViewFormDetail(authz, params = {}) {
  return canUseRecordFilters(authz, params);
}

export function canViewStatusLogs(authz, params = {}) {
  if (!params.workspaceId) return false;
  if (authz?.isFullAccess) return true;

  if (!authz?.isAuthzV2Enabled) {
    return true;
  }

  return hasRecordPermission(authz, params, RECORD.VIEW_STATUS_LOGS);
}

export function canViewImages(authz, params = {}) {
  if (!params.workspaceId) return false;
  if (authz?.isFullAccess) return true;

  if (!authz?.isAuthzV2Enabled) {
    return true;
  }

  return hasRecordPermission(authz, params, RECORD.VIEW_IMAGES);
}

export function canSendRecordTemplate(authz, params = {}) {
  if (!params.workspaceId) return false;
  if (authz?.isFullAccess) return true;

  if (!authz?.isAuthzV2Enabled) {
    return canSendWhatsappTemplate(authz);
  }

  return (
    hasRecordPermission(authz, params, RECORD.SEND_TEMPLATE)
    && canSendWhatsappTemplate(authz)
  );
}

export function canSendWhatsappTemplate(authz) {
  if (authz?.isFullAccess) return true;
  return canAccess(authz, { feature: AUTHZ_FEATURES.WHATSAPP.SEND_TEMPLATE });
}

export function canAssignLead(authz, params = {}) {
  if (!params.workspaceId) return false;
  if (authz?.isFullAccess) return true;

  if (!authz?.isAuthzV2Enabled) {
    return hasLegacyPermission(params.userInfo, params.legacyUserHasPermission, [
      globalpermissions?.leadFormPage?.assignUnAssignUser,
    ]);
  }

  return hasRecordPermission(authz, params, RECORD.ASSIGN);
}

export function canUseLeadChat(authz, params = {}) {
  if (authz?.isFullAccess) return true;

  if (!authz?.isAuthzV2Enabled) {
    return hasLegacyPermission(params.userInfo, params.legacyUserHasPermission, [
      globalpermissions?.leadDashboard?.chat,
      globalpermissions?.interveneChat?.interveneChat,
    ]);
  }

  return (
    hasRecordPermission(authz, params, RECORD.CHAT)
  );
}

export function canOpenWebForm(authz, params = {}) {
  return canUseRecordFilters(authz, params);
}
